import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { QuestRoute } from './api/QuestRoutes';
import Button from './form/Button';
import Input from './form/Input';
import Modal from './Modal';
import Card from '../_pwa/Card';
import { HamQuest } from '../../App';
import Form from './form/Form';
import Select from './form/Select';
import Loading from './Loading';

const ClubSelectorLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
`;

const ClubPrice = styled.div`
    font-size: 1.15em;
    font-weight: 700;
`;

const ClubMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.35em;
    line-height: 1.4;
`;

const ClubDescription = styled.div`
    color: ${({ theme }) => theme.mobile.text};
    opacity: 0.8;
`;

const ClubDetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1em;
    flex-wrap: wrap;
`;

const AddChildModeLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
`;

const TabBar = styled.div`
    display: flex;
    border-radius: 0.5em;
    overflow: hidden;
    border: 2px solid ${({ theme }) => theme.mobile.accent || '#333'};
`;

const Tab = styled.button`
    flex: 1;
    padding: 0.65em 1em;
    font-size: 0.95em;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: background 0.2s, color 0.2s;
    background: ${({ $active, theme }) => $active ? (theme.mobile.accent || '#333') : 'transparent'};
    color: ${({ $active, theme }) => $active ? '#fff' : (theme.mobile.text || '#333')};
    opacity: ${({ $active }) => $active ? 1 : 0.7};

    &:not(:last-child) {
        border-right: 1px solid ${({ theme }) => theme.mobile.accent || '#333'};
    }
`;

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2).replace('.00', '')}`;

const showNoChildClubOptionsError = () => {
    Modal.Show({
        type: 'error',
        title: 'Unable to Add Child Account',
        body: 'Your club does not have any options for child accounts. Please contact support for more information.'
    });
};

const checkChildEligibility = (childEmail, onChildFound) => {
    return HamQuest.get(QuestRoute.user.checkChild(childEmail))
        .then((res) => {
            if (res && res.length > 0) {
                if (onChildFound) {
                    onChildFound({
                        email: childEmail,
                        clubs: res,
                    });
                }

                return res;
            }

            showNoChildClubOptionsError();
            return null;
        });
};

const AddChildModalBody = ({ onChildFound }) => {
    const [mode, setMode] = useState('existing');
    const [isSubmittingNewUser, setIsSubmittingNewUser] = useState(false);

    if (isSubmittingNewUser) {
        return <Loading />;
    }

    return (
        <AddChildModeLayout>
            <TabBar>
                <Tab $active={mode === 'existing'} onClick={() => setMode('existing')}>
                    Invite Existing Account
                </Tab>
                <Tab $active={mode === 'new'} onClick={() => setMode('new')}>
                    Create New Account
                </Tab>
            </TabBar>

            {mode === 'existing' ? (
                <Form
                    key='existing'
                    inputs={{ email: '' }}
                    onSubmit={(e) => {
                        setIsSubmittingNewUser(true);
                        checkChildEligibility(e.email, onChildFound)
                            .then((clubs) => {
                                if (clubs) {
                                    selectClubForChild(e.email, clubs, onChildFound);
                                } else {
                                    setIsSubmittingNewUser(false);
                                }
                            })
                            .catch(ex => {
                                setIsSubmittingNewUser(false);
                                Modal.Show({
                                    type: 'error',
                                    title: 'Unable to Add Child Account',
                                    body: ex.blob || 'Sorry, something went wrong. Please try again.'
                                });
                            });
                    }}
                >
                    <Input
                        name='email'
                        label='Child Email'
                        type='email'
                        autoComplete='email'
                        autoCapitalize='none'
                        required
                    />
                </Form>
            ) : (
                <Form
                    key='new'
                    inputs={{
                        firstName: '',
                        lastName: '',
                        email: '',
                        password: '',
                        confirmpassword: '',
                        birthdayMonth: '',
                        birthdayDay: '',
                        zipPostalCode: '',
                        cellPhoneNumber: '',
                    }}
                    stickySubmit
                    onSubmit={(e) => {
                        setIsSubmittingNewUser(true);

                        const newBody = { ...e, referralCode: '', optInToMarketing: false };
                        delete newBody.confirmpassword;
                        
                        HamQuest.post(QuestRoute.user.signUp, newBody, { captcha: window.siteKey })
                            .then(() => checkChildEligibility("MadeNewUser", onChildFound))
                            .then((clubs) => {
                                if (clubs) {
                                    selectClubForChild(e.email, clubs, onChildFound);
                                } else {
                                    setIsSubmittingNewUser(false);
                                }
                            })
                            .catch(ex => {
                                setIsSubmittingNewUser(false);

                                Modal.Show({
                                    type: 'error',
                                    title: 'Unable to Create Child Account',
                                    body: ex.blob || 'Sorry, something went wrong. Please try again.'
                                });
                            });
                    }}
                >
                    <Input name='firstName' label='First Name' autoComplete='given-name' required />
                    <Input name='lastName' label='Last Name' autoComplete='family-name' required />
                    <Input
                        name='email'
                        label='Child Email'
                        type='email'
                        autoComplete='email'
                        autoCapitalize='none'
                        required
                    />
                    <Input name='password' label='Password' type='password' autoComplete='new-password' required />
                    <Input
                        name='confirmpassword'
                        label='Confirm Password'
                        type='password'
                        confirmation='password'
                        autoComplete='new-password'
                        required
                    />
                    <Input name='birthdayMonth' label='Birth Month' type='number' min={1} max={12} autoComplete='bday-month' />
                    <Input name='birthdayDay' label='Birth Day' type='number' min={1} max={31} autoComplete='bday-day' />
                    <Input name='zipPostalCode' label='Postal Code' autoComplete='postal-code' />
                    <Input name='cellPhoneNumber' label='Phone Number' autoComplete='tel' />
                </Form>
            )}
        </AddChildModeLayout>
    );
};

const ClubSelectionBody = ({ child, clubs, onChildAdded }) => {
    const [selectedClubId, setSelectedClubId] = useState(clubs[0]?.appProductId || '');
    const [isSubmittingClubSelection, setIsSubmittingClubSelection] = useState(false);

    const selectedClub = clubs.find(club => `${club.appProductId}` === `${selectedClubId}`) || clubs[0];

    const handleConfirm = () => {
        if (!selectedClub || isSubmittingClubSelection) {
            return;
        }

        setIsSubmittingClubSelection(true);

        HamQuest.post(QuestRoute.user.addChild, { email: child, clubId: selectedClub.appProductId })
            .then(() => {
                if (onChildAdded) {
                    onChildAdded({
                        email: child,
                        clubId: selectedClub.appProductId,
                    });
                }

                Modal.Show({
                    type: 'success',
                    title: 'Confirmation sent to child account',
                    body: `${child} has been invited to your Wash Club. They will receive an email with instructions on how to accept.`
                });
            })
            .catch(ex => {
                setIsSubmittingClubSelection(false);

                Modal.Show({
                    type: 'error',
                    title: 'Unable to Add Child Account',
                    body: ex.blob || 'Sorry, something went wrong. Please try again.'
                })
            });
    };

    if(isSubmittingClubSelection){
        return <Loading />;
    }

    return (
        <ClubSelectorLayout>
            <Select
                standalone
                label='Club'
                value={selectedClubId}
                onChange={(e) => setSelectedClubId(e.target.value)}
                options={clubs.map(club => ({
                    value: club.appProductId,
                    label: club.name,
                }))}
            />
            {selectedClub && (
                <Card title={selectedClub.name} icon='car' noSlide>
                    <ClubMeta>
                        <ClubPrice>Starts at {formatCurrency(selectedClub.signupPrice)}</ClubPrice>
                        <ClubDetailRow>
                            <span>Monthly price</span>
                            <strong>{formatCurrency(selectedClub.price)}/month</strong>
                        </ClubDetailRow>
                        <ClubDescription>
                            The child account invitation will be sent for this club after you confirm.
                        </ClubDescription>
                    </ClubMeta>
                </Card>
            )}
                <Button onClick={handleConfirm} icon='check' disabled={!selectedClub}>
                    Confirm Club Selection
                </Button>
        </ClubSelectorLayout>
    );
};

export const showAddChildModal = ({ onChildFound } = {}) => {
        Modal.Show({
            title: 'Add A Child Account',
            body: (
                <AddChildModalBody onChildFound={onChildFound} />
            ),
            type: 'success',
            closeAsX: true,
            closeAfter: true,
        })
};

const selectClubForChild = (child, clubs, onChildAdded) => {
        Modal.Show({
            title: `Select Club for Child`,
            body: (
                <ClubSelectionBody child={child} clubs={clubs} onChildAdded={onChildAdded} />
            ),
            type: 'success',
            closeAsX: true,
            closeAfter: true,
        })
    }

const AddChild = ({ onChildFound } = {}) => {
    useEffect(() => {
        showAddChildModal({ onChildFound });
    }, []);

    return null;
};

export default AddChild;